export type ExperimentSource = "growthbook" | "stormcrow";

export const StormcrowValues = [
  "OFF",
  "ON",
  "CONTROL",
  "TEST",
  "V1",
  "V2",
  "V3",
] as const;

// This is a common list for ON values, but is by no means exhaustive.
export const FEATURE_ON_STRING_VALUES: FeatureValue[] = [
  "ON",
  "TEST", // used to distinguish A/B test single-variant from forced "ON" variants
  "V1",
  "V2",
  "V3",
];

// This is a common list for OFF values, but is by no means exhaustive.
export const FEATURE_OFF_STRING_VALUES: FeatureValue[] = ["OFF", "CONTROL"];

/**
 * Note that the server can put any value, so this list might not always be
 * correct. If any exceptions, we'll let the dev handle special cases.
 */
export type FeatureStringValue = (typeof StormcrowValues)[number];

export type FeatureValue =
  | FeatureStringValue
  | boolean
  | number
  | object
  | undefined;

type _FeatureFlag<PossibleFeatureNames extends string> = {
  source: ExperimentSource;

  // Value of the feature.
  value?: FeatureValue;
  featureName: PossibleFeatureNames;
  userOverrideDisabled?: boolean;
  overrideValue?: FeatureValue;
  customVariants?: string[];
};

export const extensionsList = [
  {
    featureName: "dash_growth_2025_07_31_show_trial_banner_in_extension",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_04_11_report_web_vitals",
    source: "growthbook",
  },
  {
    featureName: "dash_extension_2025_01_06_side_panel_assistant",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_extension_2025_02_19_assist_actions_extension_badge",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_extension_2025_02_20_assist_actions_bar",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_extension_2025_02_13_stack_redesign",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_extension_2025_04_22_action_bar_suggestion",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_extension_2025_04_22_action_bar_suggestion_write",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_extension_2025_05_19_language_picker",
    source: "growthbook",
  },
  {
    featureName: "dash_extension_2025_06_24_edit_assist",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_2025_07_10_time_to_value",
    source: "growthbook",
  },
  {
    featureName: "dash_extension_2024_10_09_activity_tracker_minimum_duration",
    source: "growthbook",
    customVariants: ["0", "500", "1000", "2000"],
  },
  {
    featureName: "stacks_send_body_text_in_auto_stack_recommendation",
    source: "stormcrow",
  },
  {
    featureName: "stacks_send_body_text_in_work_assist",
    source: "stormcrow",
  },
  {
    featureName: "dash_should_show_development_sprig_surveys",
    source: "stormcrow",
  },
  {
    featureName: "dash_is_two_day_daily_engaged_user",
    source: "stormcrow",
  },
  {
    featureName: "dash_stacks_sharing_experiment",
    source: "stormcrow",
  },
  {
    featureName: "desktop_dash_dual_mode",
    source: "stormcrow",
  },
  {
    featureName: "dash_2025_02_18_sidepanel_assist_actions",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_04_28_pin_extension_tutorial_enabled",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_30_new_redirect_post_extension_install",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_05_01_first_action_tutorial_enabled",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_05_02_omnibox_tutorial_enabled",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_07_extension_new_conversation_side_pane",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_05_21_dnp_m5",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_07_01_dnp_m5_server",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_06_26_auto_refresh_notifications",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_11_create_stack_tab_groups",
    source: "growthbook",
    customVariants: ["ON"],
  },
  {
    featureName: "dash_2025_07_14_search_annotation_mode",
    source: "growthbook",
  },
  // Tip: Add new feature flags higher up in the list to help prevent merge conflicts
] as const satisfies Readonly<_FeatureFlag<string>[]>;

export const featuresList = [
  {
    featureName: "test_feature_ring_internal_team",
    source: "growthbook",
    userOverrideDisabled: true,
  },
  {
    featureName: "test_feature_ring_internal_dash",
    source: "growthbook",
    userOverrideDisabled: true,
  },
  {
    featureName: "test_feature_ring_internal_dbx",
    source: "growthbook",
    userOverrideDisabled: true,
  },
  {
    featureName: "test_feature_ring_external_beta",
    source: "growthbook",
    userOverrideDisabled: true,
  },
  {
    featureName: "test_feature_ring_lts",
    source: "growthbook",
    userOverrideDisabled: true,
  },

  {
    featureName: "dash_2024_06_05_august_revision",
    source: "growthbook",
  },
  {
    featureName: "dash_fullstory",
    source: "growthbook",
  },
  {
    featureName: "dash_server_result_pinning_threshold",
    source: "growthbook",
  },
  {
    featureName: "dash_typeahead_2024_06_05_suggested_query_score",
    source: "growthbook",
  },
  {
    featureName: "dash_typeahead_weights",
    source: "growthbook",
  },
  {
    featureName: "dash_link_description",
    source: "growthbook",
  },
  {
    featureName: "dash_use_dash_components",
    source: "growthbook",
  },
  {
    featureName: "dash_preview_suggested_stacks",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2024_05_09_redirect_dropboxers_staging",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_06_13_activity_feed",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_06_18_cloud_docs",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_07_04_start_page_august_revision",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_07_04_all_stacks_august_revision",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_07_04_stack_page_august_revision",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_07_29_typeahead_null_state_uses_recommendations",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_08_01_activity_feed_filter_only_mine",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_08_01_activity_feed_filter_people",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_08_06_recents_data_source",
    source: "growthbook",
  },
  {
    featureName: "context_engine_2024_08_19_experiment_setting",
    source: "growthbook",
  },
  {
    featureName: "context_engine_2025_02_10_streaming_answers",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_09_03_search_summary_enable_multimodal",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2024_09_06_compose_v1",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_09_19_typeahead_summary_row_button",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_12_09_clear_browser_history_variants",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2024_09_24_published_content_is_admin",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_09_26_summary_enable_spreadsheet",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_09_30_typeahead_filters",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2024_10_02_show_company_pinned_stacks",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_10_04_mock_recents_data",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_10_08_remove_beta_labels_for_dfb",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_10_25_increased_desktop_application_scoring",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_10_25_summary_ask_variant",
    source: "growthbook",
    customVariants: [
      "TEXTONLY_PIXTRAL_GROQ",
      "MULTIMODAL_GPT4O",
      "MULTIMODAL_PIXTRAL",
      "MULTIMODAL_QWEN",
    ],
  },
  {
    featureName: "dash_2024_10_09_show_safari",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_10_30_verified_search_results",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2024_11_13_export_to_paper",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_11_13_search_results_virtual_path",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_11_18_cold_start_notification",
    source: "growthbook",
  },
  {
    featureName:
      "dash_2024_11_21_activity_feed_fallback_collaborator_docs_threshold",
    source: "growthbook",
  },
  {
    featureName: "context_engine_2024_11_26_multimodal_file_type_filter",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_12_11_dual_mode",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_12_19_stacks_warn_on_adding_duplicate_links",
    source: "growthbook",
  },
  {
    featureName: "dash_2024_12_23_activity_feed_use_list_feed_items",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2025_01_09_assist_compose_tables",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_27_assist_context_picker",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_02_20_assist_templates",
    source: "growthbook",
  },
  {
    featureName: "dash_organize_2025_01_10_drag_pinned_stacks",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_01_31_paste_email_education_tooltip",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_01_22_stack_card_popover",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_21_serp_data_retrieval",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_28_messages_filter_channels_by_name",
    source: "growthbook",
  },
  { featureName: "dash_2025_01_24_desktop_home_page", source: "growthbook" },
  {
    featureName: "dash_2025_01_28_search_results_use_logical_type_config",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_01_29_extension_upsell_on_recents",
    source: "growthbook",
  },
  {
    featureName: "dash_search_2025_02_04_quick_links_section",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_01_30_start_page_member_invites",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_05_13_first_search_query",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_02_07_stack_remove_eligibility_check",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_23_conversation_scroll_arrow",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_02_11_conversation_assistant",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_05_08_hide_irrelevant_answers",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_02_12_assistant_file_viewer",
    source: "growthbook",
  },
  {
    featureName: "dash_admin_experience_2025_02_19_content_exclusion_feature",
    source: "growthbook",
  },
  { featureName: "dash_2025_03_05_serp_categories", source: "growthbook" },
  {
    featureName: "dash_2025_04_24_truncate_markdown_snippets",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_27_pull_chat_highlight_from_message",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_28_use_full_markdown_for_message_type",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_03_03_assistant_onboarding_demo",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_03_05_publish_company_stack_as_editor",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_03_03_search_message_results",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2025_03_11_workflow_agents_tool",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_03_18_omnibox",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_03_18_activity_feed_supported_connectors",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_03_19_assist_kv_sync_server_deletions",
    source: "growthbook",
    customVariants: ["ON", "OFF", "TEST"],
  },
  {
    featureName: "dash_2025_03_21_stack_chat",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_03_24_search_and_answer_suggestions",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_03_27_start_page_tabs",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_01_conversations_model_picker",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_01_setup_module_rollout",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_01_language_picker_languages",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_01_use_omaha4_updater",
    source: "stormcrow",
  },
  {
    featureName: "dash_2025_03_27_filter_messages_by_channel",
    source: "growthbook",
  },
  {
    featureName: "context_engine_2025_03_24_conversations_setting",
    source: "growthbook",
    customVariants: ["MULTIMODAL"],
  },
  {
    featureName: "dash_2025_03_19_slack_federated_connector",
    source: "growthbook",
  },
  {
    featureName:
      "otc_growth_2025_04_17_disable_skipping_extension_install_onboarding",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_04_17_server_driven_highlighting_only",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_05_07_stacks_startpage_show_recents_filter",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_05_06_skip_connectors_onboarding",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_05_16_create_stack_from_search",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_28_desktop_launcher_side_panel",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_06_03_keep_stack_add_modal_open",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_06_04_updated_conversation_feedback",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_27_assist_web_search",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_25_assist_ai_tool_library_prompts",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_07_31_assist_existing_template_creation",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_13_assist_incognito_mode",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_05_29_serp_filter_sidebar",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_20_serp_results_shopping_cart",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_08_omnibox_section_config",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_12_assistant_browser_tabs_picker",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2025_06_11_workflow_agents_tool_recipient",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_10_search_in_chat",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_17_start_page_tab",
    source: "growthbook",
    customVariants: ["recents", "activity", "stacks"],
  },
  {
    featureName: "dash_assist_2025_06_16_export_to_pdf",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2025_07_14_export_to_googledrive",
    source: "growthbook",
  },
  {
    featureName: "context_engine_2025_02_20_dash_calendar_agent",
    source: "growthbook",
  },
  {
    featureName: "otc_growth_2025_06_27_bridge_dash_chat_extension_upsell",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_23_omnibox_motion",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_24_fss_stacks",
    source: "growthbook",
  },
  {
    featureName: "bridge_growth_2025_06_26_fss_chat_ftux",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_27_serp_preview_modal",
    source: "growthbook",
  },
  {
    featureName: "dash_growth_2025_06_30_updated_auth_success_page",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_06_30_prioritize_find_in_null_state_typeahead",
    source: "growthbook",
  },
  {
    featureName: "dash_assist_2025_07_15_fss_ops_agent",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_07_22_result_source_icon",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_07_22_start_page_hackweek_doodle_for_dash",
    source: "growthbook",
  },
  {
    featureName: "bridge_fss_chat_2025_07_28_folder_scoped_chat",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_08_04_verified_content_search_filter",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_08_08_go_to_folder_enabled",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_07_31_ocr_search_filter",
    source: "growthbook",
  },
  {
    featureName: "dash_2025_07_15_omnibox_full_search",
    source: "growthbook",
  },
  {
    featureName: "dash_web_2025_08_08_stack_chat_namespace_id",
    source: "growthbook",
  },
  ...extensionsList,
  // Tip: Add new feature flags higher up in the list to help prevent merge conflicts
] as const satisfies Readonly<_FeatureFlag<string>[]>;

/** A union of all possible feature names */
export type FeatureName = (typeof featuresList)[number]["featureName"];

/** A fully typed feature flag, only allows valid featureNames */
export type FeatureFlag = _FeatureFlag<FeatureName>;

/** A full record of all feature flags by name */
export type FeatureFlags = Record<FeatureName, FeatureFlag>;

export const features = featuresList.reduce(
  (acc, feature) => ({
    ...acc,
    [feature.featureName]: feature,
  }),
  {} as FeatureFlags,
);
